import React, { useEffect, useState, useMemo } from 'react';
import {
  Card, Button, Table, Modal, Form, Input, Select, Space, Tag,
  Row, Col, Statistic, Typography, Tooltip, Popconfirm,
  Badge, Alert, Divider,
} from 'antd';
import {
  PlusOutlined, UserOutlined, CheckCircleOutlined,
  StopOutlined, DeleteOutlined, EditOutlined,
  SearchOutlined, CarOutlined, ClockCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useCondoStore } from '../store/condoStore';
import { useAuthStore } from '../store/authStore';
import { useAuth } from '../../../context/UnifiedAuthContext';
import { VisitorAuthorization, VisitorStatus } from '../types';
import { staticApi } from '../utils/staticApi';
import { resolveOwnerRecordKey } from '../utils/resolveOwnerKeys';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const STATUS_CONFIG: Record<VisitorStatus, { color: string; label: string }> = {
  ACTIVE:  { color: 'green',   label: 'Active'  },
  EXPIRED: { color: 'default', label: 'Expired' },
  REVOKED: { color: 'red',     label: 'Revoked' },
};

export const VisitorManagement: React.FC = () => {
  const {
    visitorAuthorizations,
    fetchVisitorAuthorizations,
    createVisitorAuthorization,
    updateVisitorAuthorization,
    revokeVisitorAuthorization,
    deleteVisitorAuthorization,
    owners,
  } = useCondoStore();
  const { currentCondo } = useAuthStore();
  const { user } = useAuth();

  const isAdmin = ['admin', 'ADMIN', 'super_admin_condo', 'SUPER_ADMIN_CONDO', 'superadmin'].includes(user?.role ?? '');
  const isOwner = user?.role === 'owner' || user?.role === 'OWNER';

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<VisitorAuthorization | null>(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [form] = Form.useForm();

  useEffect(() => {
    if (isOwner && user) {
      // Owners see only their unit's authorizations
      const owner = owners.find(o => o.email === user.email || o.id === user.id);
      fetchVisitorAuthorizations(currentCondo?.id, owner?.unitNumber);
    } else {
      fetchVisitorAuthorizations(currentCondo?.id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- condo-scoped fetch-on-mount; fetch action + derived user flags are stable for the effect
  }, [currentCondo?.id, user?.id]);

  const filtered = useMemo(() => visitorAuthorizations.filter(v => {
    const matchText = !search ||
      v.visitorName.toLowerCase().includes(search.toLowerCase()) ||
      v.unitNumber.toLowerCase().includes(search.toLowerCase()) ||
      v.ownerName.toLowerCase().includes(search.toLowerCase()) ||
      (v.vehiclePlate || '').toLowerCase().includes(search.toLowerCase());
    const matchStatus = !filterStatus || v.status === filterStatus;
    return matchText && matchStatus;
  }), [visitorAuthorizations, search, filterStatus]);

  const stats = useMemo(() => ({
    total:   visitorAuthorizations.length,
    active:  visitorAuthorizations.filter(v => v.status === 'ACTIVE').length,
    expired: visitorAuthorizations.filter(v => v.status === 'EXPIRED').length,
    revoked: visitorAuthorizations.filter(v => v.status === 'REVOKED').length,
  }), [visitorAuthorizations]);

  const handleOpen = (record?: VisitorAuthorization) => {
    setEditing(record ?? null);
    if (record) {
      form.setFieldsValue({
        ...record,
        entryFrom:  record.entryFrom?.slice(0, 10),
        entryUntil: record.entryUntil?.slice(0, 10),
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        entryFrom:  dayjs().format('YYYY-MM-DD'),
        entryUntil: dayjs().add(1, 'day').format('YYYY-MM-DD'),
        status: 'ACTIVE',
      });
    }
    setModalOpen(true);
  };

  // Sprint 136 (ADR-131) — Select value is the canonical owner key; look up by it.
  const handleOwnerSelect = (ownerId: string) => {
    const owner = owners.find(o => resolveOwnerRecordKey(o) === ownerId);
    if (owner) form.setFieldsValue({ unitNumber: owner.unitNumber, ownerName: owner.name });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = { ...values, condoId: currentCondo?.id || '' };
      if (editing) {
        await updateVisitorAuthorization(editing.id, payload);
        staticApi.message.success('Authorization updated');
      } else {
        await createVisitorAuthorization(payload);
        staticApi.message.success('Visitor authorized successfully');
      }
      setModalOpen(false);
    } catch { /* validation */ }
  };

  const handleRevoke = async (record: VisitorAuthorization) => {
    try {
      await revokeVisitorAuthorization(record.id);
      staticApi.message.success(`Authorization for ${record.visitorName} revoked`);
    } catch {
      staticApi.message.error('Failed to revoke authorization');
    }
  };

  const columns = [
    {
      title: 'Visitor',
      key: 'visitor',
      render: (_: any, r: VisitorAuthorization) => (
        <Space direction="vertical" size={0}>
          <Text strong>{r.visitorName}</Text>
          {r.visitorPhone && <Text type="secondary" style={{ fontSize: 12 }}>{r.visitorPhone}</Text>}
        </Space>
      ),
    },
    {
      title: 'Unit / Resident',
      key: 'unit',
      render: (_: any, r: VisitorAuthorization) => (
        <Space direction="vertical" size={0}>
          <Text strong>Unit {r.unitNumber}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{r.ownerName}</Text>
        </Space>
      ),
    },
    {
      title: 'Vehicle',
      dataIndex: 'vehiclePlate',
      key: 'vehicle',
      render: (v: string) => v
        ? <Space size={4}><CarOutlined style={{ color: '#8c8c8c' }} /><Text>{v}</Text></Space>
        : <Text type="secondary">—</Text>,
    },
    {
      title: 'Access Window',
      key: 'window',
      render: (_: any, r: VisitorAuthorization) => {
        const from  = dayjs(r.entryFrom);
        const until = dayjs(r.entryUntil);
        const today = dayjs();
        const overdue = until.isBefore(today, 'day') && r.status === 'ACTIVE';
        return (
          <Space direction="vertical" size={0}>
            <Text>{from.format('MMM D')} – {until.format('MMM D, YYYY')}</Text>
            {overdue && <Text type="danger" style={{ fontSize: 11 }}>Past expiry</Text>}
          </Space>
        );
      },
    },
    {
      title: 'Purpose',
      dataIndex: 'purpose',
      key: 'purpose',
      render: (v: string) => <Text type="secondary">{v || '—'}</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (v: VisitorStatus) => (
        <Badge
          status={v === 'ACTIVE' ? 'success' : v === 'REVOKED' ? 'error' : 'default'}
          text={STATUS_CONFIG[v]?.label ?? v}
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, r: VisitorAuthorization) => (
        <Space>
          <Tooltip title="Edit">
            <Button size="small" icon={<EditOutlined />} onClick={() => handleOpen(r)} />
          </Tooltip>
          {r.status === 'ACTIVE' && (
            <Tooltip title="Revoke access">
              <Button
                size="small"
                icon={<StopOutlined />}
                onClick={() => handleRevoke(r)}
                style={{ color: '#fa8c16', borderColor: '#fa8c16' }}
              />
            </Tooltip>
          )}
          <Popconfirm title="Delete this authorization?" onConfirm={() => deleteVisitorAuthorization(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <Row align="middle" gutter={12}>
            <Col>
              <div style={{
                width: 48, height: 48, borderRadius: 12,
                background: 'linear-gradient(135deg, #13c2c2, #006d75)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <UserOutlined style={{ color: '#fff', fontSize: 22 }} />
              </div>
            </Col>
            <Col>
              <Title level={3} style={{ margin: 0 }}>Visitor Management</Title>
              <Text type="secondary">
                {isAdmin ? 'All visitor authorizations across the building' : 'Manage guest access for your unit'}
              </Text>
            </Col>
          </Row>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => handleOpen()}
          style={{ borderRadius: 8, height: 38 }}
        >
          Authorize Visitor
        </Button>
      </div>

      {/* Stats */}
      <Row gutter={16} style={{ marginBottom: 20 }}>
        <Col span={6}>
          <Card><Statistic title="Total" value={stats.total} prefix={<UserOutlined />} /></Card>
        </Col>
        <Col span={6}>
          <Card><Statistic title="Active" value={stats.active} valueStyle={{ color: '#52c41a' }} prefix={<CheckCircleOutlined />} /></Card>
        </Col>
        <Col span={6}>
          <Card><Statistic title="Expired" value={stats.expired} valueStyle={{ color: '#8c8c8c' }} prefix={<ClockCircleOutlined />} /></Card>
        </Col>
        <Col span={6}>
          <Card><Statistic title="Revoked" value={stats.revoked} valueStyle={{ color: '#ff4d4f' }} prefix={<StopOutlined />} /></Card>
        </Col>
      </Row>

      {/* Today's active visitors highlight */}
      {(() => {
        const todayActive = visitorAuthorizations.filter(v =>
          v.status === 'ACTIVE' &&
          !dayjs(v.entryFrom).isAfter(dayjs(), 'day') &&
          !dayjs(v.entryUntil).isBefore(dayjs(), 'day')
        );
        return todayActive.length > 0 ? (
          <Alert
            message={`${todayActive.length} visitor${todayActive.length > 1 ? 's' : ''} authorized for access today`}
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        ) : null;
      })()}

      <Card
        title="Visitor Authorizations"
        extra={
          <Space>
            <Input
              placeholder="Search visitor, unit, plate…"
              prefix={<SearchOutlined />}
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ width: 220 }}
              allowClear
            />
            <Select
              placeholder="Status"
              allowClear
              style={{ width: 120 }}
              value={filterStatus || undefined}
              onChange={v => setFilterStatus(v ?? '')}
            >
              <Option value="ACTIVE">Active</Option>
              <Option value="EXPIRED">Expired</Option>
              <Option value="REVOKED">Revoked</Option>
            </Select>
          </Space>
        }
        bordered={false}
        style={{ borderRadius: 12, boxShadow: '0 1px 8px rgba(0,0,0,0.06)' }}
      >
        <Table
          columns={columns}
          dataSource={filtered}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          rowClassName={(r: VisitorAuthorization) =>
            r.status === 'REVOKED' ? 'ant-table-row-disabled' : ''
          }
        />
      </Card>

      {/* Create / Edit Modal */}
      <Modal
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        title={editing ? 'Edit Visitor Authorization' : 'Authorize a Visitor'}
        onOk={handleSubmit}
        okText={editing ? 'Save' : 'Authorize'}
        width={620}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
          {isAdmin && (
            <Form.Item name="ownerId" label="Resident / Unit">
              <Select
                showSearch
                placeholder="Select resident"
                optionFilterProp="label"
                onChange={handleOwnerSelect}
                options={owners.map(o => ({ label: `Unit ${o.unitNumber} — ${o.name}`, value: resolveOwnerRecordKey(o) }))}
              />
            </Form.Item>
          )}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="unitNumber" label="Unit Number" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="ownerName" label="Resident Name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Divider plain>Visitor Details</Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="visitorName" label="Visitor Name" rules={[{ required: true }]}>
                <Input placeholder="Full name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="visitorPhone" label="Visitor Phone">
                <Input placeholder="Optional" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="vehiclePlate" label="Vehicle Plate">
                <Input placeholder="e.g. ABC 1234" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="purpose" label="Purpose of Visit">
                <Input placeholder="e.g. Family stay, Contractor" />
              </Form.Item>
            </Col>
          </Row>

          <Divider plain>Access Window</Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="entryFrom" label="From Date" rules={[{ required: true }]}>
                <Input type="date" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="entryUntil" label="Until Date" rules={[{ required: true }]}>
                <Input type="date" />
              </Form.Item>
            </Col>
          </Row>

          {editing && (
            <Form.Item name="status" label="Status">
              <Select>
                <Option value="ACTIVE"><Tag color="green">Active</Tag></Option>
                <Option value="EXPIRED"><Tag>Expired</Tag></Option>
                <Option value="REVOKED"><Tag color="red">Revoked</Tag></Option>
              </Select>
            </Form.Item>
          )}

          <Form.Item name="notes" label="Notes">
            <TextArea rows={2} placeholder="Any special instructions for security…" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default VisitorManagement;
